import * as v from 'valibot';

import { InventoryConceptSchema } from './concept.ts';
import { ControlTypeSchema } from './control-type.ts';
import { VendorSchema } from './vendor.ts';

/**
 * Inventory schema — the canonical data file per panel.
 *
 * Each panel under `panels/<panel-id>/inventory.json` MUST validate
 * against this schema. The `tools/inventory-validator` CLI enforces
 * this; the build fails if any inventory drifts from the contract.
 *
 * See bootstrap brief §10.
 */

const KebabCase = v.pipe(
  v.string(),
  v.regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/u, 'Identifier must be kebab-case'),
);

const HexColour = v.pipe(
  v.string(),
  v.regex(/^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/u, 'Must be a hex colour'),
);

const ResolutionString = v.pipe(
  v.string(),
  v.regex(/^[1-9]\d*x[1-9]\d*$/u, 'Must be WIDTHxHEIGHT pixels (e.g. 4032x3024)'),
);

const FormFactor = v.picklist([
  'desk',
  'rack-1u',
  'rack-2u',
  'rack-3u',
  'tabletop',
  'integrated',
]);

const PhotoAngle = v.picklist([
  'front',
  'top',
  'oblique-30',
  'oblique-60',
  'profile',
  'rear',
  'detail',
]);

const ChassisFinish = v.picklist(['matte', 'satin', 'gloss', 'textured']);

const DefaultIlluminationState = v.picklist(['off', 'on', 'state-dependent']);

const ManualReference = v.object({
  source: v.string(),
  revision: v.string(),
  pageCount: v.pipe(v.number(), v.integer(), v.minValue(1)),
});

const PhotoReference = v.object({
  path: v.string(),
  resolution: ResolutionString,
  angle: PhotoAngle,
  notes: v.optional(v.string()),
});

const Dimensions2d = v.object({
  w: v.pipe(v.number(), v.minValue(0)),
  h: v.pipe(v.number(), v.minValue(0)),
});

const Position2d = v.object({
  x: v.number(),
  y: v.number(),
});

const BoundingBox = v.object({
  x: v.number(),
  y: v.number(),
  w: v.pipe(v.number(), v.minValue(0)),
  h: v.pipe(v.number(), v.minValue(0)),
});

const IlluminationColour = v.object({
  name: v.string(),
  hex: HexColour,
  semantics: v.string(),
});

const ControlIllumination = v.object({
  capable: v.boolean(),
  colours: v.array(IlluminationColour),
  defaultState: DefaultIlluminationState,
});

const ControlGrouping = v.object({
  group: KebabCase,
  exclusiveWith: v.optional(v.array(KebabCase)),
});

const ControlVisualVariant = v.object({
  state: v.string(),
  description: v.string(),
});

const ControlSourceReference = v.object({
  photoPath: v.string(),
  cropRegion: v.optional(BoundingBox),
});

const Control = v.object({
  id: KebabCase,
  type: ControlTypeSchema,
  label: v.string(),
  function: v.object({
    concept: InventoryConceptSchema,
    manualReference: v.object({
      page: v.pipe(v.number(), v.integer(), v.minValue(1)),
      section: v.string(),
    }),
    description: v.string(),
  }),
  position: Position2d,
  dimensions: Dimensions2d,
  rotation: v.optional(v.number()),
  illumination: v.optional(ControlIllumination),
  grouping: v.optional(ControlGrouping),
  visualVariants: v.optional(v.array(ControlVisualVariant)),
  sourceReferences: v.array(ControlSourceReference),
});

const Zone = v.object({
  id: KebabCase,
  label: v.string(),
  boundingBox: BoundingBox,
  description: v.string(),
});

const IlluminationConventions = v.object({
  program: v.string(),
  preview: v.string(),
  iso: v.optional(v.string()),
  function: v.optional(v.string()),
});

const ChromaticIdentity = v.object({
  chassisColour: HexColour,
  chassisFinish: ChassisFinish,
  accentColours: v.array(HexColour),
  illuminationConventions: IlluminationConventions,
});

const PanelMeta = v.object({
  id: KebabCase,
  vendor: VendorSchema,
  model: v.string(),
  productName: v.string(),
  formFactor: FormFactor,
  dimensionsMm: v.object({
    width: v.pipe(v.number(), v.minValue(0)),
    height: v.pipe(v.number(), v.minValue(0)),
    depth: v.pipe(v.number(), v.minValue(0)),
  }),
  canonicalViewBox: v.object({
    width: v.pipe(v.number(), v.minValue(0)),
    height: v.pipe(v.number(), v.minValue(0)),
  }),
  pixelsPerMm: v.pipe(v.number(), v.minValue(0)),
  references: v.object({
    serviceManual: v.optional(ManualReference),
    operationManual: v.optional(ManualReference),
    photos: v.array(PhotoReference),
  }),
});

export const PanelInventorySchema = v.object({
  panel: PanelMeta,
  controls: v.array(Control),
  zones: v.array(Zone),
  chromaticIdentity: ChromaticIdentity,
});

export type PanelInventory = v.InferOutput<typeof PanelInventorySchema>;
export type PanelInventoryInput = v.InferInput<typeof PanelInventorySchema>;

/**
 * Parse an unknown JSON value against the inventory schema, returning a
 * typed `PanelInventory`. Throws if validation fails — panel modules call
 * this at import time so any drift between `inventory.json` and the
 * contract surfaces in the browser console at module load, not silently
 * at render time.
 *
 * The validator CLI (`tools/inventory-validator`) is still the build-time
 * gate; this is the runtime guard that lets us drop unsafe `as unknown
 * as PanelInventory` casts from panel composition modules.
 */
export function parsePanelInventory(input: unknown): PanelInventory {
  return v.parse(PanelInventorySchema, input);
}
